import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    AreaChart, Area, LineChart, Line, BarChart, Bar,
    PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Loading from '../components/Loading';
import RechargeModal from './waba/Wallet/RechargeModal';
import { dashboardSlidesAPI } from '../services/apiService';
import { useFacility } from '../context/FacilityContext';
import config from '../config';
import './PlatformDashboard.css';

// ─── Hero Slider ─────────────────────────────────────────────────
const HeroSlider = () => {
    const [slides, setSlides] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        dashboardSlidesAPI.getActive()
            .then(r => { if (r.data.success) setSlides(r.data.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    // Auto-rotate every 6s
    useEffect(() => {
        if (slides.length <= 1) return;
        const t = setInterval(() => setCurrentIdx(i => (i + 1) % slides.length), 6000);
        return () => clearInterval(t);
    }, [slides.length]);

    const imgBase = (config.API_BASE_URL || '').replace(/\/api\/?$/, '');

    // Fallback to original hero image if no slides
    if (!loading && slides.length === 0) {
        return (
            <div className="lg:col-span-1 relative rounded-2xl shadow-card border border-gray-100/80 overflow-hidden" style={{ minHeight: 280 }}>
                <img src="/assets/images/dashboard-hero.png" alt="" className="w-full h-full object-cover select-none pointer-events-none" draggable="false" />
            </div>
        );
    }

    if (loading) {
        return <div className="lg:col-span-1 relative rounded-2xl shadow-card border border-gray-100/80 overflow-hidden bg-gray-100 animate-pulse" style={{ minHeight: 280 }} />;
    }

    const slide = slides[currentIdx];
    const handleClick = () => { if (slide.link_url) navigate(slide.link_url); };

    return (
        <div className="lg:col-span-1 relative rounded-2xl shadow-card border border-gray-100/80 overflow-hidden group" style={{ minHeight: 280 }}>
            {slides.map((s, i) => (
                <div key={s.id}
                    onClick={i === currentIdx ? handleClick : undefined}
                    className="absolute inset-0 transition-opacity duration-700"
                    style={{ opacity: i === currentIdx ? 1 : 0, cursor: i === currentIdx && s.link_url ? 'pointer' : 'default' }}>
                    <img src={`${imgBase}${s.image_url}`} alt={s.heading}
                        className="w-full h-full object-cover select-none"
                        draggable="false" />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.3) 40%, transparent 70%)' }} />
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                        <h3 className="text-lg sm:text-xl font-bold mb-1 tracking-tight">{s.heading}</h3>
                        {s.description && <p className="text-sm text-white/90 mb-3 line-clamp-2">{s.description}</p>}
                        {s.link_url && s.cta_label && (
                            <button onClick={(e) => { e.stopPropagation(); navigate(s.link_url); }}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white text-gray-900 rounded-full text-xs font-semibold shadow-md hover:bg-gray-100 transition-colors">
                                {s.cta_label}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <path d="M5 12h14M13 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            ))}

            {/* Dots */}
            {slides.length > 1 && (
                <div className="absolute bottom-2 right-4 flex gap-1.5 z-10">
                    {slides.map((_, i) => (
                        <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentIdx(i); }}
                            className="transition-all duration-300"
                            style={{
                                width: i === currentIdx ? 20 : 6,
                                height: 6,
                                borderRadius: 999,
                                background: i === currentIdx ? 'white' : 'rgba(255,255,255,0.5)',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const COLORS = ['#667eea', '#764ba2', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#14b8a6', '#ec4899'];

// Animated number counter
const AnimatedValue = ({ value, prefix = '', suffix = '' }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        const num = typeof value === 'number' ? value : parseFloat(value) || 0;
        const duration = 800;
        const steps = 30;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += increment;
            if (current >= num) {
                setDisplay(num);
                clearInterval(timer);
            } else {
                setDisplay(Math.floor(current));
            }
        }, duration / steps);
        return () => clearInterval(timer);
    }, [value]);
    return <>{prefix}{typeof value === 'number' && value % 1 !== 0 ? display.toFixed(2) : display}{suffix}</>;
};

// Staggered animation wrapper
const Stagger = ({ children, index = 0 }) => (
    <div
        className="animate-fade-in-up"
        style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
    >
        {children}
    </div>
);

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-glass-lg border border-gray-100 px-4 py-3">
            <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
                    {p.name}: {formatter ? formatter(p.value) : p.value}
                </p>
            ))}
        </div>
    );
};

const PlatformDashboard = () => {
    const fac = useFacility();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [showRechargeModal, setShowRechargeModal] = useState(false);
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => { fetchStats(); }, [fac.selectedFacilityId]);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const facParams = fac.apiParams();
            const qs = facParams.facility_id ? `?facility_id=${facParams.facility_id}` : '';
            const res = await axios.get(`${config.API_BASE_URL}/api/dashboard/stats${qs}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) setStats(res.data.stats);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) return <div className="flex items-center justify-center min-h-[60vh]"><Loading /></div>;

    const fmt = (v) => `$${parseFloat(v).toFixed(2)}`;
    const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const fmtDateTime = (dt) => new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const time = new Date(timestamp);
        const diff = Math.floor((now - time) / 1000);
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
        return `${Math.floor(diff / 86400)} days ago`;
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg> },
        { id: 'wallet', label: 'Wallet', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
        { id: 'whatsapp', label: 'WhatsApp', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg> },
        { id: 'crm', label: 'CRM & Leads', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
        { id: 'appointments', label: 'Appointments', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
        { id: 'agent', label: 'AI Agent', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
    ];

    return (
        <div className="max-w-[1600px] mx-auto animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                        Dashboard
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Welcome back, <span className="font-medium text-gray-700">{user.name || 'User'}</span></p>
                </div>
                <button
                    onClick={fetchStats}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 shadow-sm hover:shadow-md hover:border-gray-300 active:scale-[0.98] transition-all duration-200"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0118.8-4.3M22 12.5a10 10 0 01-18.8 4.2"/>
                    </svg>
                    Refresh
                </button>
            </div>

            {/* Tabs */}
            <div className="bg-gray-100 rounded-2xl p-1.5 mb-6 overflow-x-auto scrollbar-none">
                <div className="flex gap-1 min-w-max">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap
                                ${activeTab === tab.id
                                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                                    : 'bg-white text-gray-500 hover:text-gray-700 shadow-sm'
                                }`}
                        >
                            <span className={activeTab === tab.id ? 'text-white/90' : ''}>{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div key={activeTab} className="animate-fade-in-up">
                {activeTab === 'overview' && <OverviewTab stats={stats} navigate={navigate} getRelativeTime={getRelativeTime} />}
                {activeTab === 'wallet' && <WalletTab stats={stats} navigate={navigate} fmt={fmt} fmtDate={fmtDate} fmtDateTime={fmtDateTime} onRecharge={() => setShowRechargeModal(true)} />}
                {activeTab === 'whatsapp' && <WhatsAppTab stats={stats} navigate={navigate} fmtDate={fmtDate} fmtDateTime={fmtDateTime} />}
                {activeTab === 'crm' && <CRMTab stats={stats} navigate={navigate} fmtDateTime={fmtDateTime} />}
                {activeTab === 'appointments' && <AppointmentsTab stats={stats} navigate={navigate} fmtDate={fmtDate} fmtDateTime={fmtDateTime} />}
                {activeTab === 'agent' && <AgentTab stats={stats} navigate={navigate} fmt={fmt} />}
            </div>

            {showRechargeModal && (
                <RechargeModal
                    onClose={() => setShowRechargeModal(false)}
                    onSuccess={() => { setShowRechargeModal(false); fetchStats(); }}
                    currentBalance={stats?.wallet?.balance || 0}
                    rechargeLimits={null}
                />
            )}
        </div>
    );
};

// ─── Stat Card Component ─────────────────────────────────────────
const StatCard = ({ icon, iconBg, label, value, detail, badge, badgeColor, onClick, index = 0, accent }) => (
    <Stagger index={index}>
        <div
            onClick={onClick}
            className={`group relative bg-white rounded-2xl p-5 shadow-card hover:shadow-card-hover border border-gray-100/80 transition-all duration-300 hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''} overflow-hidden h-full`}
        >
            {/* Accent bar */}
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accent || 'from-primary-500 to-secondary-500'} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

            <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${iconBg} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                    {icon}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl sm:text-[26px] font-bold text-gray-900 mt-0.5 tabular-nums">
                        <AnimatedValue value={typeof value === 'string' ? parseFloat(value) || 0 : value} prefix={typeof value === 'string' && value.startsWith('$') ? '$' : ''} />
                    </p>
                    {detail && <p className="text-xs text-gray-400 mt-1">{detail}</p>}
                    {badge && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mt-1.5 ${badgeColor || 'bg-red-50 text-red-600'}`}>
                            {badge}
                        </span>
                    )}
                </div>
            </div>
        </div>
    </Stagger>
);

// ─── Premium Card Wrapper ────────────────────────────────────────
const Card = ({ children, className = '', header, action, actionLabel = 'View All', noPadding }) => (
    <div className={`bg-white rounded-2xl shadow-card border border-gray-100/80 overflow-hidden ${className}`}>
        {header && (
            <div className="flex items-center justify-between px-5 sm:px-6 pt-5 sm:pt-6 pb-0">
                <h3 className="text-base font-semibold text-gray-900">{header}</h3>
                {action && (
                    <button onClick={action} className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">
                        {actionLabel}
                    </button>
                )}
            </div>
        )}
        <div className={noPadding ? '' : 'p-5 sm:p-6'}>
            {children}
        </div>
    </div>
);

// ─── Premium Table ───────────────────────────────────────────────
const Table = ({ columns, data, onRowClick, emptyText = 'No data yet' }) => (
    <>
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-100">
                        {columns.map((col, i) => (
                            <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{col.header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {data && data.length > 0 ? data.map((row, idx) => (
                        <tr
                            key={idx}
                            onClick={() => onRowClick?.(row)}
                            className={`transition-colors duration-150 hover:bg-gray-50/80 ${onRowClick ? 'cursor-pointer' : ''}`}
                        >
                            {columns.map((col, i) => (
                                <td key={i} className="px-4 py-3.5 text-sm text-gray-700">{col.render(row, idx)}</td>
                            ))}
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-gray-400">{emptyText}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
            {data && data.length > 0 ? data.map((row, idx) => (
                <div
                    key={idx}
                    onClick={() => onRowClick?.(row)}
                    className={`bg-gray-50/80 rounded-xl p-4 space-y-2 ${onRowClick ? 'cursor-pointer active:bg-gray-100' : ''} transition-colors`}
                >
                    {columns.map((col, i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                            <span className="text-xs font-medium text-gray-500 flex-shrink-0">{col.header}</span>
                            <span className="text-sm text-gray-700 text-right">{col.render(row, idx)}</span>
                        </div>
                    ))}
                </div>
            )) : (
                <div className="text-center py-10 text-sm text-gray-400">{emptyText}</div>
            )}
        </div>
    </>
);

// ─── Badge Component ─────────────────────────────────────────────
const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/10',
        red: 'bg-red-50 text-red-700 ring-red-600/10',
        blue: 'bg-blue-50 text-blue-700 ring-blue-600/10',
        yellow: 'bg-amber-50 text-amber-700 ring-amber-600/10',
        orange: 'bg-orange-50 text-orange-700 ring-orange-600/10',
        purple: 'bg-purple-50 text-purple-700 ring-purple-600/10',
        gray: 'bg-gray-50 text-gray-600 ring-gray-500/10',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${colors[color] || colors.gray}`}>
            {children}
        </span>
    );
};

// ─── Chart Wrapper ───────────────────────────────────────────────
const ChartCard = ({ title, children, height = 280, index = 0 }) => (
    <Stagger index={index}>
        <Card>
            <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
            <div className="-mx-2">
                <ResponsiveContainer width="100%" height={height}>
                    {children}
                </ResponsiveContainer>
            </div>
        </Card>
    </Stagger>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// OVERVIEW TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const OverviewTab = ({ stats, navigate, getRelativeTime }) => {
    const leadsByStatus = Object.entries(stats.leads?.by_status || {}).map(([status, count]) => ({ status, count }));

    return (
    <div className="space-y-4">
        {/* Row 1 — Hero image + Summary text */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left — Title + highlights + Chart */}
            <div className="lg:col-span-2 flex flex-col">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Practice Overview</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {stats.patients?.total || 0} active patients have <span className="font-semibold text-gray-700">ongoing appointments</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            {stats.appointments?.today || 0} Today's Appointments
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            {stats.conversations?.unread || 0} Unread Messages
                        </span>
                    </div>
                </div>

                {/* Message Activity Chart — fills the blank space */}
                <div className="bg-white rounded-xl shadow-card border border-gray-100/80 p-4 mt-4 flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-900">Message Activity</h3>
                        <span className="text-[10px] text-gray-500 uppercase tracking-wider">Last 7 days</span>
                    </div>
                    <div style={{ height: 'calc(100% - 30px)' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={stats.messages_trend || []}>
                                <defs>
                                    <linearGradient id="overviewMsgGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#667eea" stopOpacity={0.15} />
                                        <stop offset="100%" stopColor="#667eea" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })} />
                                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="sent" stroke="#667eea" strokeWidth={2} fill="url(#overviewMsgGrad)" name="Sent" />
                                <Area type="monotone" dataKey="received" stroke="#10b981" strokeWidth={1.5} fill="transparent" name="Received" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Right — Hero Slider */}
            <Stagger index={3}>
                <HeroSlider />
            </Stagger>
        </div>

        {/* Row 2 — Wallet + 3 Stat Cards (same row) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Wallet */}
            <Stagger index={4}>
                <div onClick={() => navigate('/wallet')} className="relative bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl p-4 text-white shadow-lg cursor-pointer overflow-hidden group hover:-translate-y-0.5 transition-all duration-300 h-full">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-6 translate-x-6" />
                    <div className="relative z-10">
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-sm">💰</span>
                            <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>Balance</span>
                        </div>
                        <p className="text-2xl font-bold"><AnimatedValue value={stats.wallet?.balance || 0} prefix="$" /></p>
                        <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>💬 {(stats.messages?.sent_today || 0) + (stats.messages?.received_today || 0)} msgs today</p>
                    </div>
                </div>
            </Stagger>

            {[
                { icon: '👤', label: 'Total Patients', value: stats.patients?.total || 0, change: `+${stats.patients?.new_this_month || 0}`, path: '/patients' },
                { icon: '📊', label: 'Conversion', value: `${stats.leads?.converted || 0}%`, change: `${stats.leads?.total || 0} leads`, path: '/crm/leads' },
                { icon: '📅', label: 'Appointments', value: stats.appointments?.total || 0, change: `${stats.appointments?.upcoming || 0} upcoming`, path: '/appointments?status=scheduled' },
            ].map((s, i) => (
                <Stagger key={i} index={i + 5}>
                    <div onClick={() => navigate(s.path)} className="bg-white rounded-xl p-4 shadow-card border border-gray-100/80 h-full cursor-pointer hover:-translate-y-0.5 hover:shadow-card-hover transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-xs">{s.icon}</span>
                            <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{s.label}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-gray-900">{typeof s.value === 'number' ? <AnimatedValue value={s.value} /> : s.value}</span>
                            <span className="text-[11px] font-semibold text-emerald-600">{s.change}</span>
                        </div>
                        <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" style={{ width: '65%' }} />
                        </div>
                    </div>
                </Stagger>
            ))}
        </div>

        {/* Row 3 — Lead Distribution (full width) */}
        <Stagger index={8}>
            <div className="bg-white rounded-xl shadow-card border border-gray-100/80 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Lead Distribution</h3>
                {leadsByStatus.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-2">
                        {leadsByStatus.slice(0, 5).map((item, idx) => {
                            const max = Math.max(...leadsByStatus.map(l => l.count), 1);
                            const pct = Math.round((item.count / max) * 100);
                            const colors = ['#667eea', '#764ba2', '#10b981', '#f59e0b', '#ef4444'];
                            return (
                                <div key={idx}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[11px] text-gray-600 capitalize">{item.status}</span>
                                        <span className="text-[11px] font-bold text-gray-900">{item.count}</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 text-center py-4">No lead data yet</p>
                )}
            </div>
        </Stagger>

        {/* Row 3 — Conversations + Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
                { label: 'New Lead', icon: '➕', path: '/crm/leads/new', gradient: 'from-indigo-500 to-purple-500' },
                { label: 'Book Appointment', icon: '📅', path: '/appointments/new', gradient: 'from-teal-500 to-emerald-500' },
                { label: 'Send Campaign', icon: '📢', path: '/waba/campaigns/new', gradient: 'from-blue-500 to-indigo-500' },
                { label: 'Conversations', icon: '💬', path: '/waba/conversations', gradient: 'from-emerald-500 to-green-500' },
            ].map((item, idx) => (
                <div key={idx} onClick={() => navigate(item.path)}
                    className="group relative bg-white rounded-lg p-3 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 overflow-hidden border border-gray-100/80">
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    <div className="relative z-10 flex items-center gap-2">
                        <span className="text-base group-hover:scale-110 transition-transform">{item.icon}</span>
                        <span className="text-xs font-semibold text-gray-700 group-hover:text-white transition-colors">{item.label}</span>
                    </div>
                </div>
            ))}
        </div>

        {/* Activity + Alerts Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Activity Feed */}
            <Stagger index={9}>
                <Card className="xl:col-span-2 max-h-[500px] overflow-hidden flex flex-col" noPadding>
                    <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
                        <h3 className="text-base font-semibold text-gray-900">Recent Activity</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 space-y-2 scrollbar-thin">
                        {stats.activity_feed && stats.activity_feed.length > 0 ? stats.activity_feed.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 transition-transform group-hover:scale-110
                                    ${item.type === 'message' ? 'bg-blue-50' : item.type === 'lead' ? 'bg-indigo-50' : item.type === 'appointment' ? 'bg-teal-50' : 'bg-amber-50'}`}>
                                    {item.type === 'message' ? '💬' : item.type === 'lead' ? '👤' : item.type === 'appointment' ? '📅' : '💰'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                                    <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">{getRelativeTime(item.timestamp)}</p>
                                </div>
                            </div>
                        )) : (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                <div className="text-4xl mb-2 opacity-50">📭</div>
                                <p className="text-sm">No recent activity</p>
                            </div>
                        )}
                    </div>
                </Card>
            </Stagger>

            {/* Alerts */}
            <Stagger index={10}>
                <div className="space-y-3">
                    <h3 className="text-base font-semibold text-gray-900 px-1">Alerts</h3>

                    {stats.alerts?.low_balance && (
                        <div onClick={() => navigate('/wallet')} className="group flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-100/80 cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                            <div className="text-2xl">⚠️</div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">Low Wallet Balance</h4>
                                <p className="text-xs text-gray-600 mt-0.5">Your balance is below the threshold. Recharge now.</p>
                            </div>
                        </div>
                    )}

                    {stats.alerts?.overdue_follow_ups > 0 && (
                        <div onClick={() => navigate('/crm/leads?follow_up=overdue')} className="group flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100/80 cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                            <div className="text-2xl">🕐</div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">{stats.alerts.overdue_follow_ups} Overdue Follow-ups</h4>
                                <p className="text-xs text-gray-600 mt-0.5">You have pending follow-ups to complete</p>
                            </div>
                        </div>
                    )}

                    {stats.alerts?.unread_messages > 0 && (
                        <div onClick={() => navigate('/waba/conversations')} className="group flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-100/80 cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                            <div className="text-2xl">📩</div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">{stats.alerts.unread_messages} Unread Messages</h4>
                                <p className="text-xs text-gray-600 mt-0.5">New messages waiting for response</p>
                            </div>
                        </div>
                    )}

                    {stats.alerts?.todays_appointments > 0 && (
                        <div onClick={() => navigate('/appointments')} className="group flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100/80 cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                            <div className="text-2xl">📅</div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">{stats.alerts.todays_appointments} Appointments Today</h4>
                                <p className="text-xs text-gray-600 mt-0.5">View today's schedule</p>
                            </div>
                        </div>
                    )}

                    {!stats.alerts?.low_balance && !stats.alerts?.overdue_follow_ups && !stats.alerts?.unread_messages && !stats.alerts?.todays_appointments && (
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100/80">
                            <div className="text-2xl">✅</div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">All Good!</h4>
                                <p className="text-xs text-gray-600 mt-0.5">No pending actions required</p>
                            </div>
                        </div>
                    )}
                </div>
            </Stagger>
        </div>
    </div>
    );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WALLET TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const WalletTab = ({ stats, navigate, fmt, fmtDate, fmtDateTime, onRecharge }) => (
    <div className="space-y-6">
        {/* Wallet Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Stagger index={0}>
                <div className="relative bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg shadow-emerald-500/25 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-6 -translate-x-6" />
                    <div className="relative z-10">
                        <p className="text-emerald-100 text-xs font-medium uppercase tracking-wider">Current Balance</p>
                        <p className="text-3xl font-bold mt-1">{fmt(stats.wallet?.balance || 0)}</p>
                        <button onClick={onRecharge} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-white text-emerald-600 hover:bg-gray-50 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 shadow-sm">
                            Recharge Now
                        </button>
                    </div>
                </div>
            </Stagger>
            <StatCard index={1} icon="📉" iconBg="bg-red-50" label="Total Spent" value={stats.wallet?.total_spent || 0} accent="from-red-400 to-red-600" />
            <StatCard index={2} icon="📈" iconBg="bg-blue-50" label="Total Recharged" value={stats.wallet?.total_recharged || 0} accent="from-blue-400 to-blue-600" />
            <StatCard index={3} icon="⚠️" iconBg="bg-amber-50" label="Low Balance Threshold" value={stats.wallet?.low_balance_threshold || 0} accent="from-amber-400 to-amber-600" />
        </div>

        {/* Spending Chart */}
        <ChartCard title="Spending Trend (Last 30 Days)" index={4}>
            <AreaChart data={stats.spending_trend || []}>
                <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip content={<CustomTooltip formatter={(v) => `$${v.toFixed(2)}`} />} />
                <Area type="monotone" dataKey="amount" stroke="#ef4444" strokeWidth={2} fill="url(#spendGrad)" />
            </AreaChart>
        </ChartCard>

        {/* Transactions Table */}
        <Stagger index={5}>
            <Card header="Recent Transactions" action={() => navigate('/wallet/transactions')} noPadding>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <Table
                        columns={[
                            { header: 'Date', render: (r) => <span className="text-gray-500">{fmtDateTime(r.created_at)}</span> },
                            { header: 'Type', render: (r) => {
                                const isCredit = ['recharge','credit','admin_credit','refund'].includes(r.type);
                                return <Badge color={isCredit ? 'green' : 'red'}>{r.type.replace(/_/g, ' ')}</Badge>;
                            }},
                            { header: 'Description', render: (r) => <span className="truncate max-w-[200px] block">{r.description || '-'}</span> },
                            { header: 'Amount', render: (r) => {
                                const isCredit = ['recharge','credit','admin_credit','refund'].includes(r.type);
                                return <span className={`font-semibold ${isCredit ? 'text-emerald-600' : 'text-red-600'}`}>{isCredit ? '+' : '-'}{fmt(Math.abs(r.amount))}</span>;
                            }},
                        ]}
                        data={stats.recent_transactions?.slice(0, 10)}
                        emptyText="No transactions yet"
                    />
                </div>
            </Card>
        </Stagger>
    </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WHATSAPP TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const WhatsAppTab = ({ stats, navigate, fmtDate, fmtDateTime }) => (
    <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard index={0} icon="📨" iconBg="bg-blue-50" label="Total Sent" value={stats.messages?.total_sent || 0} accent="from-blue-400 to-blue-600" onClick={() => navigate('/waba/conversations')} />
            <StatCard index={1} icon="📥" iconBg="bg-emerald-50" label="Total Received" value={stats.messages?.total_received || 0} accent="from-emerald-400 to-emerald-600" onClick={() => navigate('/waba/conversations')} />
            <StatCard index={2} icon="💬" iconBg="bg-amber-50" label="Total Conversations" value={stats.conversations?.total || 0} accent="from-amber-400 to-amber-600" onClick={() => navigate('/waba/conversations?status=open')} />
            <StatCard index={3} icon="📋" iconBg="bg-purple-50" label="Approved Templates" value={stats.templates?.approved || 0} accent="from-purple-400 to-purple-600" onClick={() => navigate('/waba/templates?status=approved')} />
        </div>

        <ChartCard title="Message Activity (Last 7 Days)" index={4}>
            <LineChart data={stats.messages_trend || []}>
                <defs>
                    <linearGradient id="lineSent" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#6366f1" />
                    </linearGradient>
                    <linearGradient id="lineRecv" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#14b8a6" />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="sent" stroke="url(#lineSent)" strokeWidth={2.5} name="Sent" dot={{ fill: '#3b82f6', strokeWidth: 0, r: 4 }} activeDot={{ r: 6, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="received" stroke="url(#lineRecv)" strokeWidth={2.5} name="Received" dot={{ fill: '#10b981', strokeWidth: 0, r: 4 }} activeDot={{ r: 6, fill: '#10b981' }} />
            </LineChart>
        </ChartCard>

        <Stagger index={5}>
            <Card header="Recent Conversations" action={() => navigate('/waba/conversations')} noPadding>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <Table
                        columns={[
                            { header: 'Contact', render: (r) => <span className="font-medium">{r.contact_name || 'Unknown'}</span> },
                            { header: 'Phone', render: (r) => r.contact_phone },
                            { header: 'Last Message', render: (r) => <span className="truncate max-w-[180px] block text-gray-500">{r.last_message || '-'}</span> },
                            { header: 'Time', render: (r) => <span className="text-gray-500">{fmtDateTime(r.last_message_at)}</span> },
                            { header: 'Unread', render: (r) => r.unread_count > 0 ? <Badge color="red">{r.unread_count}</Badge> : <span className="text-gray-300">-</span> },
                            { header: 'Status', render: (r) => <Badge color={r.status === 'open' ? 'green' : 'gray'}>{r.status}</Badge> },
                        ]}
                        data={stats.recent_conversations?.slice(0, 10)}
                        onRowClick={(r) => navigate(`/waba/conversations/${r.id}`)}
                        emptyText="No conversations yet"
                    />
                </div>
            </Card>
        </Stagger>
    </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CRM TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const CRMTab = ({ stats, navigate, fmtDateTime }) => {
    const leadsByStatus = Object.entries(stats.leads?.by_status || {}).map(([status, count]) => ({ status, count }));
    const leadsBySource = Object.entries(stats.leads?.by_source || {}).map(([source, count]) => ({ source, count }));

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard index={0} icon="👥" iconBg="bg-indigo-50" label="Total Leads" value={stats.leads?.total || 0} accent="from-indigo-400 to-indigo-600" onClick={() => navigate('/crm/leads')} />
                <StatCard index={1} icon="✅" iconBg="bg-emerald-50" label="Converted" value={stats.leads?.converted || 0} accent="from-emerald-400 to-emerald-600" onClick={() => navigate('/crm/leads?status=won')} />
                <StatCard index={2} icon="📅" iconBg="bg-amber-50" label="Follow-ups Today" value={stats.leads?.follow_ups_today || 0} accent="from-amber-400 to-amber-600" onClick={() => navigate('/crm/leads?follow_up=today')} />
                <StatCard index={3} icon="⏰" iconBg="bg-red-50" label="Overdue" value={stats.leads?.overdue_follow_ups || 0} accent="from-red-400 to-red-600" onClick={() => navigate('/crm/leads?follow_up=overdue')} />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                <ChartCard title="Leads by Status" index={4}>
                    <BarChart data={leadsByStatus}>
                        <defs>
                            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#667eea" />
                                <stop offset="100%" stopColor="#764ba2" />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="status" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                    </BarChart>
                </ChartCard>

                <ChartCard title="Leads by Source" index={5}>
                    <PieChart>
                        <Pie data={leadsBySource} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3} label>
                            {leadsBySource.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                    </PieChart>
                </ChartCard>

                <ChartCard title="Lead Conversion Funnel" index={6}>
                    <BarChart data={stats.lead_funnel || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                        <YAxis dataKey="stage" type="category" tick={{ fontSize: 11, fill: '#9ca3af' }} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="url(#barGrad)" radius={[0, 6, 6, 0]} />
                    </BarChart>
                </ChartCard>
            </div>

            <Stagger index={7}>
                <Card header="Recent Leads" action={() => navigate('/crm/leads')} noPadding>
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                        <Table
                            columns={[
                                { header: 'Name', render: (r) => <span className="font-medium">{r.full_name}</span> },
                                { header: 'Phone', render: (r) => r.phone },
                                { header: 'Source', render: (r) => <Badge color="blue">{r.source}</Badge> },
                                { header: 'Status', render: (r) => <Badge color={r.status === 'won' ? 'green' : r.status === 'lost' ? 'red' : 'yellow'}>{r.status}</Badge> },
                                { header: 'Priority', render: (r) => <Badge color={r.priority === 'high' ? 'red' : r.priority === 'medium' ? 'orange' : 'gray'}>{r.priority}</Badge> },
                                { header: 'Assigned', render: (r) => r.assigned_to_name },
                                { header: 'Created', render: (r) => <span className="text-gray-500">{fmtDateTime(r.created_at)}</span> },
                            ]}
                            data={stats.recent_leads?.slice(0, 10)}
                            emptyText="No leads yet"
                        />
                    </div>
                </Card>
            </Stagger>
        </div>
    );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// APPOINTMENTS TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const AppointmentsTab = ({ stats, navigate, fmtDate, fmtDateTime }) => (
    <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard index={0} icon="📅" iconBg="bg-teal-50" label="Total Appointments" value={stats.appointments?.total || 0} accent="from-teal-400 to-teal-600" onClick={() => navigate('/appointments')} />
            <StatCard index={1} icon="📆" iconBg="bg-blue-50" label="Today" value={stats.appointments?.today || 0} accent="from-blue-400 to-blue-600" onClick={() => navigate('/appointments?filter=today')} />
            <StatCard index={2} icon="✅" iconBg="bg-emerald-50" label="Completed" value={stats.appointments?.completed || 0} accent="from-emerald-400 to-emerald-600" onClick={() => navigate('/appointments?status=completed')} />
            <StatCard index={3} icon="🔔" iconBg="bg-amber-50" label="Upcoming" value={stats.appointments?.upcoming || 0} accent="from-amber-400 to-amber-600" onClick={() => navigate('/appointments?status=scheduled')} />
        </div>

        <ChartCard title="Appointment Completion Trend (Last 7 Days)" index={4}>
            <LineChart data={stats.appointment_completion_trend || []}>
                <defs>
                    <linearGradient id="schedLine" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#6366f1" />
                    </linearGradient>
                    <linearGradient id="compLine" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="scheduled" stroke="url(#schedLine)" strokeWidth={2.5} name="Scheduled" dot={{ fill: '#3b82f6', strokeWidth: 0, r: 4 }} />
                <Line type="monotone" dataKey="completed" stroke="url(#compLine)" strokeWidth={2.5} name="Completed" dot={{ fill: '#10b981', strokeWidth: 0, r: 4 }} />
            </LineChart>
        </ChartCard>

        <Stagger index={5}>
            <Card header="Today's Appointments" action={() => navigate('/appointments')} noPadding>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <Table
                        columns={[
                            { header: 'Time', render: (r) => <span className="font-medium text-gray-900">{r.appointment_time}</span> },
                            { header: 'Patient', render: (r) => r.patient_name },
                            { header: 'Doctor', render: (r) => r.doctor_name },
                            { header: 'Type', render: (r) => <Badge color="purple">{r.appointment_type}</Badge> },
                            { header: 'Status', render: (r) => <Badge color={r.status === 'completed' ? 'green' : r.status === 'cancelled' ? 'red' : 'blue'}>{r.status}</Badge> },
                        ]}
                        data={stats.todays_appointments}
                        emptyText="No appointments today"
                    />
                </div>
            </Card>
        </Stagger>
    </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AI AGENT TAB
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const AgentTab = ({ stats, navigate, fmt }) => {
    const agentByChannel = Object.entries(stats.agent?.by_channel || {}).map(([channel, count]) => ({ channel, count }));

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard index={0} icon="🤖" iconBg="bg-purple-50" label="Total Conversations" value={stats.agent?.total_conversations || 0} accent="from-purple-400 to-purple-600" onClick={() => navigate('/settings/ai-agent')} />
                <StatCard index={1} icon="💬" iconBg="bg-blue-50" label="Today" value={stats.agent?.conversations_today || 0} accent="from-blue-400 to-blue-600" onClick={() => navigate('/settings/ai-agent')} />
                <StatCard index={2} icon="💰" iconBg="bg-red-50" label="Total Cost" value={stats.agent?.total_cost || 0} accent="from-red-400 to-red-600" />
            </div>

            <ChartCard title="Conversations by Channel" index={3}>
                <BarChart data={agentByChannel}>
                    <defs>
                        <linearGradient id="agentBar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#667eea" />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="channel" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="url(#agentBar)" radius={[6, 6, 0, 0]} />
                </BarChart>
            </ChartCard>

            <Stagger index={4}>
                <Card>
                    <h3 className="text-base font-semibold text-gray-900 mb-4">AI Agent Statistics</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { label: 'Web Widget', key: 'web_widget', gradient: 'from-blue-500 to-indigo-500' },
                            { label: 'Web Voice', key: 'web_voice', gradient: 'from-purple-500 to-pink-500' },
                            { label: 'Voice Call', key: 'voice_call', gradient: 'from-emerald-500 to-teal-500' },
                            { label: 'WhatsApp', key: 'whatsapp', gradient: 'from-green-500 to-emerald-500' },
                        ].map((item, idx) => (
                            <div key={idx} className="group relative bg-gray-50 rounded-xl p-4 text-center hover:bg-gradient-to-br hover:shadow-lg transition-all duration-300 overflow-hidden">
                                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                                <div className="relative z-10">
                                    <p className="text-xs font-medium text-gray-500 group-hover:text-white/80 transition-colors">{item.label}</p>
                                    <p className="text-2xl font-bold text-gray-900 group-hover:text-white mt-1 transition-colors">
                                        {stats.agent?.by_channel?.[item.key] || 0}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </Stagger>
        </div>
    );
};

export default PlatformDashboard;
